import { Component } from '@theme/component';

/**
 * Masaüstü header menüsü (PK-04).
 * Fareyle hover ve klavye odağı CSS'te açar; bu bileşen tıklama ve dokunmatik durumlarını yönetir:
 * - "#" linkli üst öğeler buton olarak render edilir ve tıklayınca açılıp kapanır.
 * - Gerçek URL'li üst öğelerde dokunmatik ilk dokunuş menüyü açar, ikinci dokunuş linke gider.
 * - Escape ve dışarı tıklama açık menüyü kapatır.
 *
 * @extends {Component}
 */
class PkHeaderNav extends Component {
  /** @type {string} */
  #lastPointerType = 'mouse';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#rememberPointer);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('pointerover', this.#handlePointerOver);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#rememberPointer);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('pointerover', this.#handlePointerOver);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Bir üst öğenin alt menüsünü açar ya da kapatır.
   * @param {MouseEvent} event
   */
  toggle(event) {
    const trigger = /** @type {HTMLElement} */ (event.target);
    const item = trigger.closest('.pk-nav__item, .pk-nav__subitem');
    if (!(item instanceof HTMLElement)) return;

    const isLink = trigger instanceof HTMLAnchorElement;
    const isOpen = item.hasAttribute('data-open');

    if (isLink) {
      // Fare ve klavyede gerçek link normal çalışır: menü hover/odak ile zaten açık.
      // Dokunmatikte ilk dokunuş menüyü açar, açıkken ikinci dokunuş linke gider.
      const isTouchTap = this.#lastPointerType !== 'mouse' && event.detail !== 0;
      if (!isTouchTap || isOpen) return;
    }

    event.preventDefault();
    if (isOpen) {
      this.#close(item);
    } else {
      this.#closeSiblings(item);
      this.#open(item);
    }
  }

  /** @param {PointerEvent} event */
  #rememberPointer = (event) => {
    this.#lastPointerType = event.pointerType || 'mouse';
  };

  /**
   * Fare başka bir üst öğeye geçtiğinde tıklamayla açık kalan menüyü kapatır.
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = /** @type {HTMLElement} */ (event.target).closest?.('.pk-nav__item');
    if (!(item instanceof HTMLElement)) return;
    for (const open of this.querySelectorAll('.pk-nav__item[data-open]')) {
      if (open !== item && open instanceof HTMLElement) this.#close(open);
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const openItems = [...this.querySelectorAll('[data-open]')];
    const focused = document.activeElement;
    const item = focused?.closest('.pk-nav__item');
    for (const open of openItems) if (open instanceof HTMLElement) this.#close(open);
    if (item) {
      const trigger = item.querySelector(':scope > .pk-nav__link');
      if (trigger instanceof HTMLElement) {
        trigger.focus();
        // Odak tetikleyicide kaldığı sürece :focus-within paneli açık tutmasın.
        item.setAttribute('data-dismissed', '');
        trigger.addEventListener('blur', () => item.removeAttribute('data-dismissed'), { once: true });
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (this.contains(/** @type {Node} */ (event.target))) return;
    for (const open of this.querySelectorAll('[data-open]')) {
      if (open instanceof HTMLElement) this.#close(open);
    }
  };

  /** @param {HTMLElement} item */
  #open(item) {
    item.setAttribute('data-open', '');
    item.removeAttribute('data-dismissed');
    item.querySelector(':scope > [aria-expanded]')?.setAttribute('aria-expanded', 'true');
  }

  /** @param {HTMLElement} item */
  #close(item) {
    item.removeAttribute('data-open');
    item.querySelector(':scope > [aria-expanded]')?.setAttribute('aria-expanded', 'false');
    for (const child of item.querySelectorAll('[data-open]')) {
      if (child instanceof HTMLElement) this.#close(child);
    }
  }

  /** @param {HTMLElement} item */
  #closeSiblings(item) {
    const parent = item.parentElement;
    if (!parent) return;
    for (const sibling of parent.children) {
      if (sibling !== item && sibling instanceof HTMLElement && sibling.hasAttribute('data-open')) {
        this.#close(sibling);
      }
    }
  }
}

if (!customElements.get('pk-header-nav')) {
  customElements.define('pk-header-nav', PkHeaderNav);
}

/**
 * Mobil menü çekmecesi (PK-04).
 * Header'ın altından, soldan açılır. Her menü seviyesi ayrı bir paneldir; `go` ileri, `back` geri gider.
 *
 * @typedef {{ toggle: HTMLButtonElement, panel: HTMLElement }} PkHeaderDrawerRefs
 * @extends {Component<PkHeaderDrawerRefs>}
 */
class PkHeaderDrawer extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {MediaQueryList} */
  #desktop = window.matchMedia('(min-width: 1024px)');

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    this.#desktop.addEventListener('change', this.#handleBreakpoint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#desktop.removeEventListener('change', this.#handleBreakpoint);
    this.#unlockScroll();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const header = this.closest('#header-component');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--pk-drawer-top', `${top}px`);

    this.#showView('root', false);
    this.setAttribute('open', '');
    this.refs.panel.inert = false;
    this.#setToggleState(true);
    this.#lockScroll();

    const first = this.refs.panel.querySelector('[data-view="root"] .pk-drawer__link');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.refs.panel.inert = true;
    this.#setToggleState(false);
    this.#unlockScroll();
    this.refs.toggle.focus({ preventScroll: true });
  }

  /**
   * Bir alt menü paneline geçer.
   * @param {MouseEvent} event
   */
  go(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-target]');
    if (!(button instanceof HTMLElement) || !button.dataset.target) return;
    this.#showView(button.dataset.target, true);
  }

  /** Bir üst panele döner. */
  back() {
    const active = this.refs.panel.querySelector('.pk-drawer__view[data-active]');
    if (!(active instanceof HTMLElement)) return;
    const parent = active.dataset.parent || 'root';
    this.#showView(parent, true, active.dataset.view);
  }

  /**
   * @param {string} name - Gösterilecek panelin `data-view` değeri
   * @param {boolean} focus - Yeni paneldeki ilk öğeye odaklan
   * @param {string} [returnTo] - Geri dönüşte odaklanacak tetikleyicinin hedefi
   */
  #showView(name, focus, returnTo) {
    const views = this.refs.panel.querySelectorAll('.pk-drawer__view');
    const target = this.refs.panel.querySelector(`.pk-drawer__view[data-view="${name}"]`);
    if (!(target instanceof HTMLElement)) return;

    // Hedefin ata zinciri sola kayar, diğerleri sağda bekler.
    const ancestors = new Set();
    let parent = target.dataset.parent;
    while (parent) {
      ancestors.add(parent);
      const parentView = this.refs.panel.querySelector(`.pk-drawer__view[data-view="${parent}"]`);
      parent = parentView instanceof HTMLElement ? parentView.dataset.parent : undefined;
    }

    for (const view of views) {
      if (!(view instanceof HTMLElement)) continue;
      const isTarget = view === target;
      view.toggleAttribute('data-active', isTarget);
      view.toggleAttribute('data-behind', ancestors.has(view.dataset.view));
      view.inert = !isTarget;
    }
    target.scrollTop = 0;

    if (!focus) return;
    const selector = returnTo ? `[data-target="${returnTo}"]` : '.pk-drawer__back, .pk-drawer__link';
    const next = target.querySelector(selector);
    if (next instanceof HTMLElement) next.focus({ preventScroll: true });
  }

  /** @param {boolean} open */
  #setToggleState(open) {
    const { toggle } = this.refs;
    toggle.setAttribute('aria-expanded', String(open));
    const label = open ? toggle.dataset.labelClose : toggle.dataset.labelOpen;
    if (label) toggle.setAttribute('aria-label', label);
  }

  #lockScroll() {
    document.documentElement.classList.add('pk-scroll-lock');
  }

  #unlockScroll() {
    document.documentElement.classList.remove('pk-scroll-lock');
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.#trapFocus(event);
  };

  /**
   * Odağı açık çekmece ile kapatma butonu arasında tutar.
   * @param {KeyboardEvent} event
   */
  #trapFocus(event) {
    const active = this.refs.panel.querySelector('.pk-drawer__view[data-active]');
    const focusables = [
      this.refs.toggle,
      ...(active ? active.querySelectorAll('a[href], button:not([disabled])') : []),
    ].filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
    if (focusables.length === 0) return;

    const first = /** @type {HTMLElement} */ (focusables[0]);
    const last = /** @type {HTMLElement} */ (focusables[focusables.length - 1]);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #handleBreakpoint = () => {
    if (this.#desktop.matches) this.close();
  };
}

if (!customElements.get('pk-header-drawer')) {
  customElements.define('pk-header-drawer', PkHeaderDrawer);
}
